// encumbrance-heal.ts — R-209 (Wizard v9 WP2, 2026-10-09): the page DECIDES a creature's weight-tier encumbrance (Encumbered / Heavily
// Encumbered / Immobile) when its capacity is the browser's (the carrying_capacity_multiplier C-PI fold — Gravity "Density Shift"), and
// persists the decision through the status commit (status_truth_commit_inc.php status_add / status_delete). PHP's finalize steps aside for
// exactly those rows (finalize_creature_arr.php ft_encumbrance_weight_tiers_browser_owned — the same layer-PRESENCE test the C-PI
// collection reports as `capacityOwned`), so the two never fight.
//
// Inventory weight deltas settle one creature at a time, one write per pass.
// The decision lives in compile/creature-carrying-capacity.ts; unavailable
// campaign or capacity inputs leave the row unchanged.
import { decideWeightEncumbrance, heldWeightEncumbrance, WEIGHT_ENCUMBRANCE_STATUS_IDS,
  type WeightEncumbranceTier } from '../janus-engine/compile/creature-carrying-capacity.js';
import { tryCommitStatusAdd, tryCommitStatusDelete } from './status-truth.js';
import { rowHoldsStatusItem } from '../janus-engine/statuses/gms-tools.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => { let v = value; while (Array.isArray(v)) v = v[0]; return v; };

/** ft_campaign_context() as the page can answer it: a PC's attached campaign first, else its encounter's master parent when that parent is
 *  a CAMPAIGN (a location / adventure parent = no campaign = no house rules → null). undefined = a campaign the page does not hold. */
function campaignOf(row: Row): Row | null | undefined {
  const ft = (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown>; campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } }).FateTide;
  const lookup = (id: string): Row | null | undefined => {
    const key = (c: Row | null): string => upper(asRow(c?.campaigns)?.campaign_id ?? c?.campaign_id);
    const match = [asRow(ft?.campaignCache?.get(id) ?? ft?.campaignCache?.get(id.toLowerCase())), asRow(ft?.currentCampaignArr)].find(c => c && key(c) === id);
    if (!match) return undefined;
    if (match.campaign_house_rules !== undefined && !Array.isArray(match.campaign_house_rules)) return undefined;
    return match;
  };
  const attached = upper(scalar(asRow(row.player_characters)?.attached_campaign_id));
  if (attached) { const c = lookup(attached); if (c !== undefined) return c; }
  const encounterId = upper(asRow(row.encounter_creatures)?.encounter_id);
  const encounter = asRow(encounterId ? ft?.encounterCache?.get(encounterId) ?? ft?.encounterCache?.get(encounterId.toLowerCase()) : null);
  const enc = asRow(encounter?.encounters) ?? encounter;
  if (!enc) return undefined;
  const type = String(scalar(enc.encounter_master_parent_type) ?? '').toLowerCase();
  const parent = upper(scalar(enc.encounter_master_parent_id));
  if (type !== 'campaign' || !parent) return attached ? undefined : null;
  return lookup(parent);
}

const inflight = new Set<string>();
const pending = new Map<string, Row>();
export const encumbranceHealLog: Array<{ root: string; tier: WeightEncumbranceTier; action: 'add' | 'remove'; outcome: string; at: number }> = [];

function mayAct(row: Row): boolean {
  const perms = asRow(row.permissions);
  return perms?.campaign === true || perms?.encounter === true || perms?.player === true || Number(perms?.player ?? 0) === 1;
}
/** The next single write the newest row needs, or null when the held tiers already match (or the row is undecidable). */
function nextStep(row: Row): { tier: WeightEncumbranceTier; action: 'add' | 'remove'; statusId?: string } | null {
  const desired = decideWeightEncumbrance({ row, campaign: campaignOf(row),
    layerValue: (r, name) => ftLayerValue(r, name), layerEntries: (r, name) => { const v = ftLayerEntries(r, name); return Array.isArray(v) ? v : []; } });
  if (!desired) return null;
  const held = heldWeightEncumbrance(row);
  for (const tier of Object.keys(WEIGHT_ENCUMBRANCE_STATUS_IDS) as WeightEncumbranceTier[]) {
    if (!desired[tier] && held[tier].length) return { tier, action: 'remove', statusId: held[tier][0] };
    if (desired[tier] && !rowHoldsStatusItem(row, WEIGHT_ENCUMBRANCE_STATUS_IDS[tier])) return { tier, action: 'add' };
  }
  return null;
}

/** Decide + persist one creature's weight tiers (`owned` = the C-PI collection's layer-presence verdict). Returns what it did. */
export async function healWeightEncumbrance(row: Row, rootId: string, owned: boolean): Promise<string> {
  const root = upper(rootId);
  if (!owned) return 'not-owned';
  if (!mayAct(row)) return 'not-controller';
  pending.set(root, row);
  if (inflight.has(root)) return 'queued'; // the running pass re-decides from this newer row before its next write
  inflight.add(root);
  const done: string[] = [];
  try {
    // one write per step, each decided from the NEWEST settled row; a bounded number of steps per pass (3 tiers × add / remove)
    for (let i = 0; i < 6; i++) {
      const current = pending.get(root)!;
      const step = nextStep(current);
      if (!step) break;
      const encounterId = upper(asRow(current.encounter_creatures)?.encounter_id);
      const outcome = step.action === 'add'
        ? await tryCommitStatusAdd({ targetId: root, statusItemId: WEIGHT_ENCUMBRANCE_STATUS_IDS[step.tier],
          originId: WEIGHT_ENCUMBRANCE_STATUS_IDS[step.tier], durationInt: 0, durationType: 'permanent', encounterId })
        : await tryCommitStatusDelete({ statusId: step.statusId!, scope: 'single' });
      encumbranceHealLog.push({ root, tier: step.tier, action: step.action, outcome: String(outcome), at: Date.now() });
      if (encumbranceHealLog.length > 40) encumbranceHealLog.splice(0, encumbranceHealLog.length - 40);
      done.push(`${step.action}-${step.tier}:${String(outcome)}`);
      // ONE write per pass: the status lands through its own frame, which changes the row's generation — the healer settles it again and
      // the next tier is decided from that saved row (never from an optimistic copy that might re-add the same tier)
      break;
    }
  } finally { inflight.delete(root); pending.delete(root); }
  return done.length ? 'posted:' + done.join(',') : 'current';
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  const host = window as unknown as { __ftEncumbranceHealMounted?: boolean; ftEncumbranceHealState?: () => unknown };
  if (!host.__ftEncumbranceHealMounted) {
    host.__ftEncumbranceHealMounted = true;
    host.ftEncumbranceHealState = () => ({ log: [...encumbranceHealLog], inflight: [...inflight] });
    // Saved newborns can cross a weight tier without a full creature compile.
    // As in compiled encumbrance retirement, the encounter GM settles these rows.
    window.addEventListener('ft:inventory-weight-changed', event => {
      const detail = asRow((event as CustomEvent).detail), row = asRow(detail?.row);
      const permissions = asRow(row?.permissions);
      if (!row || permissions?.campaign !== true && permissions?.encounter !== true) return;
      void healWeightEncumbrance(row, String(detail?.owner ?? ''), true).catch(() => undefined);
    });

  }
}
